/**
 * Meta Pixel (Facebook Pixel) tracking utilities
 */

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
    };
  }
}

const META_PIXEL_ID = '1488370568915261';

/**
 * Verifica se Meta Pixel è caricato
 */
export const isMetaPixelLoaded = (): boolean => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Traccia evento generico Meta Pixel
 */
export const trackMetaPixelEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (!isMetaPixelLoaded()) {
    console.warn('Meta Pixel non caricato');
    return;
  }

  try {
    window.fbq('track', eventName, params);
    if (import.meta.env.DEV) {
      console.log(`Meta Pixel Event: ${eventName}`, params);
    }
  } catch (error) {
    console.error('Errore nel tracking Meta Pixel:', error);
  }
};

/**
 * Traccia visualizzazione pagina
 */
export const trackPageView = () => {
  trackMetaPixelEvent('PageView');
};

/**
 * Traccia visualizzazione prodotto
 */
export const trackViewContent = (product: {
  content_name: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}) => {
  trackMetaPixelEvent('ViewContent', {
    content_name: product.content_name,
    content_ids: product.content_ids || [product.content_name],
    content_type: product.content_type || 'product',
    value: product.value,
    currency: product.currency || 'EUR',
  });
};

/**
 * Traccia aggiunta al carrello
 */
export const trackAddToCart = (product: {
  content_name: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
  quantity?: number;
}) => {
  trackMetaPixelEvent('AddToCart', {
    content_name: product.content_name,
    content_ids: product.content_ids || [product.content_name],
    content_type: product.content_type || 'product',
    value: product.value,
    currency: product.currency || 'EUR',
    quantity: product.quantity || 1,
  });
};

/**
 * Traccia inizio checkout
 */
export const trackInitiateCheckout = (data: {
  value?: number;
  currency?: string;
  num_items?: number;
  content_ids?: string[];
  contents?: Array<{ id: string; quantity: number }>;
}) => {
  trackMetaPixelEvent('InitiateCheckout', {
    value: data.value,
    currency: data.currency || 'EUR',
    num_items: data.num_items,
    content_ids: data.content_ids,
    contents: data.contents,
  });
};

/**
 * Traccia aggiunta informazioni di pagamento
 */
export const trackAddPaymentInfo = (data: {
  value?: number;
  currency?: string;
  content_ids?: string[];
  contents?: Array<{ id: string; quantity: number }>;
}) => {
  trackMetaPixelEvent('AddPaymentInfo', {
    value: data.value,
    currency: data.currency || 'EUR',
    content_ids: data.content_ids,
    contents: data.contents,
  });
};

/**
 * Traccia acquisto completato
 */
export const trackPurchase = (data: {
  value: number;
  currency?: string;
  content_ids?: string[];
  contents?: Array<{ id: string; quantity: number }>;
  num_items?: number;
  order_id?: string;
}) => {
  trackMetaPixelEvent('Purchase', {
    value: data.value,
    currency: data.currency || 'EUR',
    content_ids: data.content_ids,
    contents: data.contents,
    num_items: data.num_items,
    order_id: data.order_id,
  });
};

/**
 * Traccia ricerca prodotti
 */
export const trackSearch = (searchTerm: string) => {
  trackMetaPixelEvent('Search', {
    search_string: searchTerm,
  });
};

